<template>
  <SimpleDataWithVariation
    :title="title"
    :item-id="itemId"
    :value="value"
    :variation="variation"
    :raw-variation="rawVariation"
    :request-status="requestStatus"
    :comparation-value="comparationValue"
    v-bind="$attrs"
    class="make-a-copy"
    @reload="getData"
  />
</template>

<script>
import { mapActions } from 'vuex';
import { formatSocialNumberWithDecimal } from '@/utils/formatter';
import { DATA_TYPE } from '@/store/modules/cards/constants';
import SimpleDataWithVariation from './models/SimpleDataWithVariation.vue';
import sacListeningModel from '@/mixins/sac-listening-model';
export default {
  name: 'PostSamplingProjection',
  components: {
    SimpleDataWithVariation,
  },
  mixins: [
    sacListeningModel,
  ],
  props: {
    title: {
      type: String,
      required: true,
    },
    itemId: {
      type: [String, Number],
      required: true,
    },
    dataType: {
      type: String,
      required: true,
      validator: value => ['SAC', 'LISTENING'].includes(value),
    },
  },
  data() {
    return {
      requestStatus: 'loading',
      showGlobalFilterModal: false,
      filterButton: this.$t('components.OperatorsLogs.filterLabel'),
      value: '',
      variation: '',
      rawVariation: '',
      comparationValue: '',
    };
  },
  created() {
    this.getData();
  },
  methods: {
    ...mapActions('cards', [
      'getSacListeningWarroomService',
    ]),
    getData(options) {
      if (!options?.silentRefresh) {
        this.requestStatus = 'loading';
      }
      const type = this.dataType === 'SAC' ? DATA_TYPE.SAC : DATA_TYPE.LISTENING;
      const serviceConfigs = [
        { filterType: type, serviceParams: { favorite: true } },
        { filterType: type, serviceParams: { noFilter: true } },
        { filterType: type, serviceParams: { totalFavorite: true } },
      ];
      const promises = serviceConfigs.map(cfg =>
        this.getSacListeningWarroomService({
          itemId: this.itemId,
          servicePath: 'monitoringOverview',
          filterType: cfg.filterType,
          serviceParams: cfg.serviceParams,
        })
      );
      Promise.all(promises).then(([responseFavorite, responseTotalNoFilter, responseTotalFavorite]) => {
        const projection = this.calculateProjection(
          responseFavorite.collected_mentions,
          responseTotalNoFilter.collected_mentions,
          responseTotalFavorite.collected_mentions
        );
        this.value = projection;
        const projectionVariation = this.calculateProjection(
          responseFavorite.collected_mentions_variation,
          responseTotalNoFilter.collected_mentions_variation,
          responseTotalFavorite.collected_mentions_variation
        );
        this.variation = projectionVariation;
        this.rawVariation = projectionVariation;
        const previousProjection = this.calculateProjection(
          responseFavorite.previous_collected_mentions,
          responseTotalNoFilter.previous_collected_mentions,
          responseTotalFavorite.previous_collected_mentions
        );
        this.comparisonValue = formatSocialNumberWithDecimal(previousProjection);
        this.requestStatus = 'success';
      }).catch((error) => {
        console.error('Error fetching data for PostSamplingProjection:', error);
        this.requestStatus = 'error';
      });
    },
    /**
     * Calculates the projected value of SAC listening posts based on favorite (checked) and total counts.
     *
     * The projection formula estimates the expected number of posts in the full dataset
     * that would be "favorite" (checked) (i.e., meet a certain criteria), based on the observed ratio
     * in a filtered subset.
     *
     * @param {number} responseFavorite - The number of "favorite" (checked) posts in the filtered subset.
     * @param {number} responseTotalNoFilter - The total number of posts in the unfiltered (full) dataset.
     * @param {number} responseTotalFavorite - The total number of posts in the filtered subset.
     * @returns {number} The projected number of "favorite" (checked) posts in the full dataset.
     *
     * Formula:
     *   projection = responseTotalNoFilter * (responseFavorite / responseTotalFavorite)
     * Where (responseFavorite / responseTotalFavorite) is the observed ratio in the filtered subset.
     */
    calculateProjection(responseFavorite, responseTotalNoFilter, responseTotalFavorite) {
      if (
        responseTotalFavorite === undefined ||
        responseTotalFavorite === null ||
        responseTotalFavorite === 0
      ) {
        return 0;
      }
      const postsCheckedDivision = responseFavorite / responseTotalFavorite;
      const result = responseTotalNoFilter * postsCheckedDivision;
      return result;
    },
  },
};
</script>
<style scoped lang="scss">
</style>
